import React, { useMemo, useState } from 'react';
import type { DayHourCell, DayHourMetric, VisitsAnalyticsQuery, VisitsHeatmapCell } from '@troia-v3/types';

import { ViewToggle } from '@/shared/components/ui';
import { DashCard, formatNumber } from '@/shared/components/ui/dashboard';

import { useDayHour } from '../hooks/useVisitsAnalytics';

interface Props {
  /** Heatmap de visitas que já veio no payload da página — não é refeito. */
  visits: VisitsHeatmapCell[];
  /** Período e recortes ativos, repassados quando a métrica muda. */
  query: VisitsAnalyticsQuery;
}

const DAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
/** Sem dado nenhum, mostra a faixa de operação típica de loja em shopping. */
const DEFAULT_HOURS = { first: 9, last: 22 };

const METRIC_LABEL: Record<DayHourMetric, { noun: string; empty: string }> = {
  visits: { noun: 'visitas', empty: 'Nenhuma visita medida no período.' },
  leads: { noun: 'leads', empty: 'Nenhum lead criado no período.' },
  sales: { noun: 'vendas', empty: 'Nenhuma venda fechada no período.' },
};

/**
 * Intensidade via `color-mix` sobre o primário do tema — sem roxo hardcoded,
 * então acompanha o white-label do cliente e o tema escuro.
 */
const cellColor = (pct: number): string => {
  if (pct <= 0) return 'transparent';
  const mix = Math.min(100, Math.max(8, Math.round(pct * 100)));
  return `color-mix(in srgb, var(--theme-primary) ${mix}%, transparent)`;
};

const formatCurrency = (value: number): string =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

/**
 * Concentração por dia e hora — a mesma grade para visitas, leads e vendas.
 *
 * Agnóstica à métrica de propósito: a pergunta "que combinação de dia e hora
 * concentra o movimento" é a mesma para as três, e responder com três
 * componentes quase iguais garantiria que eles divergissem com o tempo.
 *
 * As visitas vêm prontas do payload da página; leads e vendas são buscados só
 * quando o botão muda. A janela de horas é DERIVADA do dado, não fixa: leads
 * chegam de madrugada e uma faixa fixa de horário comercial os esconderia sem
 * aviso.
 */
export const DayHourConcentrationCard: React.FC<Props> = ({ visits, query }) => {
  const [metric, setMetric] = useState<DayHourMetric>('visits');

  const { data, isLoading } = useDayHour({ ...query, metric }, metric !== 'visits');

  const cells: DayHourCell[] = useMemo(() => {
    if (metric === 'visits') {
      return visits.map((c) => ({ dayOfWeek: c.dayOfWeek, hour: c.hour, value: c.views }));
    }
    return data?.cells ?? [];
  }, [metric, visits, data]);

  const byCell = useMemo(
    () => new Map(cells.map((c) => [`${c.dayOfWeek}-${c.hour}`, c])),
    [cells],
  );
  const max = Math.max(1, ...cells.map((c) => c.value));

  // Janela derivada do dado. Sem nenhuma célula preenchida, cai no horário
  // comercial — uma grade de 24 colunas vazias não diz nada a ninguém.
  const hours = useMemo(() => {
    const withValue = cells.filter((c) => c.value > 0).map((c) => c.hour);
    const first = withValue.length > 0 ? Math.min(...withValue) : DEFAULT_HOURS.first;
    const last = withValue.length > 0 ? Math.max(...withValue) : DEFAULT_HOURS.last;
    return Array.from({ length: last - first + 1 }, (_, i) => first + i);
  }, [cells]);

  const labels = METRIC_LABEL[metric];
  const isEmpty = !isLoading && cells.every((c) => c.value === 0);

  return (
    <DashCard
      icon="grid"
      title="Concentração por dia e hora"
      subtitle="Onde o movimento se concentra na semana"
      testId="analytics-day-hour-card"
      headerRight={
        <ViewToggle
          modes={[
            { id: 'visits', icon: 'eye', label: 'Visitas' },
            { id: 'leads', icon: 'users', label: 'Leads' },
            { id: 'sales', icon: 'dollar-sign', label: 'Vendas' },
          ]}
          value={metric}
          onChange={(id) => setMetric(id as DayHourMetric)}
          collapsibleLabels={false}
          data-testid="analytics-day-hour-metric"
        />
      }
      noPadding
    >
      {isLoading ? (
        <div
          className="py-10 text-center text-sm"
          style={{ color: 'var(--theme-muted-text)' }}
          data-testid="analytics-day-hour-loading"
        >
          Carregando {labels.noun}…
        </div>
      ) : isEmpty ? (
        <div
          className="py-10 text-center text-sm"
          style={{ color: 'var(--theme-muted-text)' }}
          data-testid="analytics-day-hour-empty"
        >
          {labels.empty}
        </div>
      ) : (
        <div className="overflow-x-auto p-4">
          <table className="w-full border-separate" style={{ borderSpacing: '2px', minWidth: 560 }}>
            <thead>
              <tr>
                <th className="text-left text-xs font-medium pr-2" style={{ color: 'var(--theme-muted-text)' }} />
                {hours.map((h) => (
                  <th key={h} className="text-[10px] font-medium" style={{ color: 'var(--theme-muted-text)' }}>
                    {String(h).padStart(2, '0')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAYS.map((day, index) => {
                const dow = index + 1;
                return (
                  <tr key={day}>
                    <td
                      className="text-xs font-medium pr-2 whitespace-nowrap"
                      style={{ color: 'var(--theme-muted-text)' }}
                    >
                      {day}
                    </td>
                    {hours.map((hour) => {
                      const cell = byCell.get(`${dow}-${hour}`);
                      const value = cell?.value ?? 0;
                      const revenueSuffix =
                        cell?.revenue !== undefined && cell.revenue > 0
                          ? ` · ${formatCurrency(cell.revenue)}`
                          : '';
                      return (
                        <td
                          key={hour}
                          className="rounded text-center text-[10px] h-7"
                          style={{
                            backgroundColor: cellColor(value / max),
                            color: value / max > 0.55 ? '#ffffff' : 'var(--theme-muted-text)',
                            border: value === 0 ? '1px dashed var(--theme-card-border)' : 'none',
                          }}
                          title={`${day} ${String(hour).padStart(2, '0')}h — ${formatNumber(value)} ${labels.noun}${revenueSuffix}`}
                          data-testid={`analytics-day-hour-cell-${dow}-${hour}`}
                        >
                          {/* Célula vazia mostra travessão, não "0": pode ser loja
                              fechada, e afirmar zero seria inventar informação. */}
                          {value > 0 ? formatNumber(value) : '—'}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DashCard>
  );
};

DayHourConcentrationCard.displayName = 'DayHourConcentrationCard';
